import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';

import { StatusChip } from '@/design';
import { formatDateTime } from '@/domains/survey';
import type {
  IAdminDisclosure,
  IAdminDisclosureChange,
  IAdminDisclosureSource
} from '../api/participant-contracts';
import {
  changeKindWords,
  disclosureField,
  listedByWords,
  NOT_LISTED_WORDS,
  originWords,
  sectionTitle,
  sfObjectWords,
  type DisclosureFieldKind
} from '../disclosure-fields';
import { absent } from './account-facts';
import { formatDay } from './days';
import { formatMoney } from './sec-adv-values';
import { TableFrame } from './table-frame';

/**
 * One disclosure, drawn the one way on both screens that show one (SR-396's D2) — the hub's
 * Disclosures, under each CRD of a member's set, and a disclosure work item's pane, which draws
 * the disclosure its item is about. A row — its day, type and resolution, its allegations clamped
 * to two lines, and a chip per registry listing it — with everything else folded under it and kept
 * whole: the sections as sent, the Salesforce copies, the history.
 *
 * **Two renderings would drift**: the pane was a link to the hub and a table of raw member keys,
 * and drawing the card again there would mean a second set of labels, a second reading of a value
 * and a second chip, each of which would agree with the hub's until it did not. So the screen
 * owns only what is the screen's — the root element and its anchor (the hub's per-member `li`, the
 * pane's `div` with none), whether it opens folded, CMP-14's `hidden`, the heading level — and the
 * pane hands in the codes its item's change moved (`changed`), each such field marked
 * `data-changed` with "Changed" beside its label, never by colour alone.
 *
 * **The chips are `listedBy`'s, in survey-api's order** — "On BrokerCheck", "On IAPD", or "No
 * longer listed" when it is empty (the owner's Q1). A disclosure read without `listedBy` (an API
 * before SR-396) is drawn from `live` alone, which then meant BrokerCheck — never as listed by no
 * registry. `[data-disclosure]` is on the root on both screens; `[data-listed-by]` holds the codes
 * the chips were drawn from, space-separated, empty when none is.
 */
export function DisclosureCard({
  disclosure,
  as: Root = 'div',
  id,
  hidden = false,
  defaultOpen = false,
  level,
  changed
}: {
  disclosure: IAdminDisclosure;
  /** The root element: the hub's list item, the pane's block. */
  as?: 'li' | 'div';
  /** The root's anchor — the hub's per-member `disclosureAnchor`; none on the pane. */
  id?: string;
  /** Hidden by CMP-14's switch — still in the document, for a reveal to find. */
  hidden?: boolean;
  /** Opens the fold as it first draws — the pane, where the disclosure is what is reviewed. */
  defaultOpen?: boolean;
  /** The level of the fold's part headings. */
  level: DisclosureLevel;
  /** The codes the reviewed change moved, marked on the fields that carry them. */
  changed?: readonly string[];
}) {
  const { eventDate, type, resolution, allegations } = disclosure;
  const moved = new Set(changed ?? []);
  return (
    <Root
      id={id}
      hidden={hidden}
      data-disclosure={disclosure.id}
      className="scroll-mt-4 rounded-md border border-border px-3 py-2 text-sm lg:scroll-mt-[calc(3.5rem+var(--section-bar-h,2.5rem))]"
    >
      {/* `open` only as the card first draws: a later render with the same value leaves the
          reader's own fold alone. */}
      <details open={defaultOpen || undefined}>
        <summary className="cursor-pointer">
          <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1 align-middle">
            <span>{eventDate === null ? absent('Undated') : formatDay(eventDate)}</span>
            <span aria-hidden>·</span>
            <span className="font-medium">{type ?? absent('No type given')}</span>
            {resolution !== null && (
              <>
                <span aria-hidden>·</span>
                <span>{resolution}</span>
              </>
            )}
            <ListedChips disclosure={disclosure} />
          </span>
          {allegations !== null && (
            <span data-allegations className="mt-1 line-clamp-2 whitespace-pre-line text-muted-foreground">
              {allegations}
            </span>
          )}
        </summary>
        <div className="mt-3 grid gap-4">
          {disclosure.sections.map((section) => (
            <Part key={section.code} title={sectionTitle(section.code)} level={level}>
              <div className="grid gap-3 sm:grid-cols-2">
                {section.fields.map((field) => (
                  <FieldFact
                    key={field.code}
                    code={field.code}
                    value={field.value}
                    changed={moved.has(field.code)}
                  />
                ))}
              </div>
            </Part>
          ))}
          <Part title="Salesforce copies" level={level}>
            <Sources sources={disclosure.sources} />
          </Part>
          <Part title="History" level={level}>
            <History changes={disclosure.changes} level={deeperLevel(level)} />
          </Part>
        </div>
      </details>
    </Root>
  );
}

/** The registries a disclosure is listed by, as `listedBy` names them — or, from an API before
 *  SR-396, as `live` then meant (BrokerCheck). */
function listedByOf({ listedBy, live }: IAdminDisclosure): readonly string[] {
  if (listedBy !== undefined) return listedBy;
  return live ? ['brokercheck'] : [];
}

/** One chip per registry listing the disclosure, `attention` — or one `neutral` "No longer
 *  listed". */
function ListedChips({ disclosure }: { disclosure: IAdminDisclosure }) {
  const registries = listedByOf(disclosure);
  return (
    <span data-listed-by={registries.join(' ')} className="inline-flex flex-wrap gap-1">
      {registries.length === 0 ? (
        <StatusChip tone="neutral">{NOT_LISTED_WORDS}</StatusChip>
      ) : (
        registries.map((registry) => (
          <StatusChip key={registry} tone="attention">
            {listedByWords(registry)}
          </StatusChip>
        ))
      )}
    </span>
  );
}

/** A heading level inside a disclosure: 3 on a household of one, deeper under a member's name or
 *  a pane's section. */
export type DisclosureLevel = 3 | 4 | 5 | 6;

export function DisclosureHeading({
  level,
  id,
  className,
  children
}: {
  level: DisclosureLevel;
  id?: string;
  className?: string;
  children: React.ReactNode;
}) {
  const Tag = `h${level}` as const;
  return (
    <Tag id={id} className={className}>
      {children}
    </Tag>
  );
}

export function deeperLevel(level: DisclosureLevel): DisclosureLevel {
  return Math.min(level + 1, 6) as DisclosureLevel;
}

/** A titled part of a disclosure's fold — a group named by its title, so a spec and a reader
 *  find "Customer Dispute" or "History" by what it is called. */
function Part({
  title,
  level,
  children
}: {
  title: string;
  level: DisclosureLevel;
  children: React.ReactNode;
}) {
  const id = React.useId();
  return (
    <div role="group" aria-labelledby={id} data-part className="grid gap-2">
      <DisclosureHeading level={level} id={id} className="text-xs font-semibold tracking-wide uppercase">
        {title}
      </DisclosureHeading>
      {children}
    </div>
  );
}

/** One field, named by its label — or, for a code this app has no label for, its code — and,
 *  where the reviewed change moved it, "Changed" said beside the label. */
function FieldFact({ code, value, changed }: { code: string; value: unknown; changed: boolean }) {
  const id = React.useId();
  const field = disclosureField(code);
  return (
    <div
      role="group"
      aria-labelledby={id}
      data-field={code}
      data-changed={changed || undefined}
      className="grid content-start gap-0.5"
    >
      <span className="flex flex-wrap items-baseline gap-x-2">
        {field ? (
          <span id={id} className="text-xs text-muted-foreground">
            {field.label}
          </span>
        ) : (
          <code id={id} className="text-xs text-muted-foreground">
            {code}
          </code>
        )}
        {changed && <span className="text-xs font-medium text-[var(--color-royal-blue)]">Changed</span>}
      </span>
      <div data-value className="whitespace-pre-line wrap-anywhere">
        {valueText(field?.kind, value)}
      </div>
    </div>
  );
}

/**
 * A value as it reads (R5) — by its JSON type first, so a value whose type is not its member's
 * reads as what it is, and the kind refines it: a day for a date, dollars for an amount (cents
 * kept, since a judgement can carry them). Text keeps its line breaks, which the caller's
 * `whitespace-pre-line` draws. A section's fields are never `false` or null — absent instead —
 * but a change's old and new values can be: `false` is "No" and null is "None".
 */
function valueText(kind: DisclosureFieldKind | undefined, value: unknown): string {
  if (value === null || value === undefined) return 'None';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') {
    if (kind !== 'money') return String(value);
    return formatMoney(value);
  }
  if (typeof value === 'string') return kind === 'date' ? formatDay(value) : value;
  return JSON.stringify(value);
}

/** The Salesforce records the disclosure was merged from, newest modified first — their ids as
 *  text to select, never a link: no Salesforce address is configured, and Salesforce is being
 *  switched off. */
function Sources({ sources }: { sources: IAdminDisclosureSource[] }) {
  if (sources.length === 0) return <p>{absent('No Salesforce copy is on record.')}</p>;
  return (
    <ul aria-label="Salesforce copies" className="grid gap-2">
      {sources.map((source) => (
        <li key={source.sfId} data-sf-id={source.sfId} className="grid gap-0.5">
          <span className="flex flex-wrap items-baseline gap-x-2">
            <span>{sfObjectWords(source.sfObject)}</span>
            <code className="text-xs select-all">{source.sfId}</code>
            {source.sfParentId !== null && (
              <span className="text-xs text-muted-foreground">
                filed under <code className="select-all">{source.sfParentId}</code>
              </span>
            )}
          </span>
          {source.deletedUpstreamAt !== null && (
            <span className="text-muted-foreground">
              Deleted in Salesforce on {instantDay(source.deletedUpstreamAt)}
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}

/** An instant as the day it falls on in the reader's zone — `formatDateTime` without the time,
 *  which a deletion upstream does not need. Not `days.ts`' `formatDay`: that one reads a
 *  `yyyy-MM-dd` with no zone, and this is a stamp. */
export function instantDay(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** What survey-api journalled, newest first — each change's kind, who observed it and when, and
 *  for a change that moved members, each one before and after. */
function History({ changes, level }: { changes: IAdminDisclosureChange[]; level: DisclosureLevel }) {
  if (changes.length === 0) return <p>{absent('No change is journalled.')}</p>;
  return (
    <ol aria-label="History" className="grid gap-3">
      {changes.map((change, index) => (
        <li key={`${change.occurredAt} ${index}`} data-change className="grid gap-2">
          <DisclosureHeading level={level} className="flex flex-wrap items-baseline gap-x-2">
            <span className="font-medium">{changeKindWords(change.kind)}</span>
            <span className="text-muted-foreground">
              {originWords(change.origin)} · {formatDateTime(change.occurredAt)}
            </span>
          </DisclosureHeading>
          {change.changed !== null && <ChangedFields changed={change.changed} label="Members changed" />}
        </li>
      ))}
    </ol>
  );
}

/**
 * The members a change moved, each labelled through `disclosure-fields.ts` (a member it has no
 * label for by its code) with its value before and after, in the order stored — a history entry's
 * table on the card, and the pane's "What changed". `label` is the table's accessible name, which
 * each screen's specs find it by.
 */
export function ChangedFields({
  changed,
  label
}: {
  changed: NonNullable<IAdminDisclosureChange['changed']>;
  label: string;
}) {
  return (
    <TableFrame>
      <Table aria-label={label}>
        <TableHeader>
          <TableRow>
            <TableHead>Field</TableHead>
            <TableHead>Before</TableHead>
            <TableHead>After</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {Object.entries(changed).map(([code, { old, new: next }]) => {
            const field = disclosureField(code);
            return (
              <TableRow key={code} data-field={code}>
                <TableCell>{field ? field.label : <code>{code}</code>}</TableCell>
                <TableCell className="whitespace-pre-line">{valueText(field?.kind, old)}</TableCell>
                <TableCell className="whitespace-pre-line">{valueText(field?.kind, next)}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableFrame>
  );
}
